import React, { useId, useMemo } from "react";
import PropTypes from "prop-types";
import FormControl from "@mui/material/FormControl";
import {
  StyledAutocomplete,
  StyledAutocompleteTextField,
  StyledSelect,
} from "./Dropdown.style";

export const Dropdown = ({
  displayValue,
  value,
  handleChange,
  borderColor,
  textColor,
  searchable = false,
  options = [],
  children,
  onInputChange,
  id: idProp,
  labelId: labelIdProp,
}) => {
  const generatedId = useId();
  const id = idProp ?? `dropdown-${generatedId.replace(/:/g, "")}`;
  const labelId = labelIdProp ?? `dropdown-label-${generatedId.replace(/:/g, "")}`;

  const dropdownOptions = useMemo(() => {
    if (searchable && options.length === 0 && children) {
      return React.Children.map(children, (child) => ({
        value: child.props.value,
        label: child.props.children,
      }));
    }
    return options;
  }, [searchable, options, children]);

  const selectedOption = useMemo(() => {
    if (!searchable) return null;
    if (value === "" || value == null) {
      return null;
    }
    return dropdownOptions.find((opt) => String(opt.value) === String(value)) ?? null;
  }, [searchable, dropdownOptions, value]);

  const handleAutocompleteChange = (event, newValue) => {
    if (newValue && handleChange) {

      const syntheticEvent = {
        target: {
          value: newValue.value,
        },
      };
      handleChange(syntheticEvent);
    } else if (!newValue && handleChange) {

      const syntheticEvent = {
        target: {
          value: "",
        },
      };
      handleChange(syntheticEvent);
    }
  };


  const handleInputChange = (event, newInputValue) => {
    if (onInputChange) {
      onInputChange(newInputValue);
    }
  };

  if (searchable) {
    return (
      <FormControl fullWidth>
        <StyledAutocomplete
          borderColor={borderColor}
          autoHighlight={false}
          value={selectedOption}
          onChange={handleAutocompleteChange}
          onInputChange={handleInputChange}
          options={dropdownOptions}
          getOptionLabel={(option) => option?.label ?? ""}
          getOptionDisabled={(option) => Boolean(option?.disabled)}
          isOptionEqualToValue={(option, val) =>
            val != null &&
            typeof val === "object" &&
            String(option?.value) === String(val?.value)
          }
          renderInput={(params) => (
            <StyledAutocompleteTextField
              {...params}
              borderColor={borderColor}
              textColor={textColor}
              placeholder={selectedOption ? "" : displayValue}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
        />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth>
      <StyledSelect
        borderColor={borderColor}
        textColor={textColor}
        labelId={labelId}
        id={id}
        value={value || ""}
        onChange={handleChange}
        displayEmpty
        renderValue={value}
      >
        {children}
      </StyledSelect>
    </FormControl>
  );
};

Dropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleChange: PropTypes.func.isRequired,
  borderColor: PropTypes.string,
  searchable: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  children: PropTypes.node,
  onInputChange: PropTypes.func,
  id: PropTypes.string,
  labelId: PropTypes.string,
};
Dropdown.defaultProps = {
  textColor: "white",
  searchable: false,
  children: null,
  onInputChange: () => {},
  id: undefined,
  labelId: undefined,
};
